import React from 'react';
import { logger } from '@marriott/mi-headless-utils';
import type { SlotContent } from './DynamicTabbedLayout.types';
import { resolveSlotComponent } from '../../utils/resolveSlotComponent';
import type { SlotTrackingContext } from '../../utils/slotPropNormalizerUtils';
import { buildRetargetingCfFallbackSlot } from '../../utils/retargeting/retargetingRuntime';
import {
  RETARGETING_FALLBACK_RENDER_ERROR_EVENT,
  RETARGETING_SLOT_RENDER_ERROR_EVENT,
} from '../../utils/retargeting/retargeting.constants';

const { log } = logger({})('RetargetingSlotErrorBoundary');

type BoundaryMode = 'primary' | 'fallback';

type RetargetingSlotErrorBoundaryProps = {
  slot: SlotContent;
  trackingCtx?: SlotTrackingContext;
  resetKey: string;
  children?: React.ReactNode;
};

type RetargetingSlotErrorBoundaryState = {
  mode: BoundaryMode;
};

function SlotResolverChild({
  slot,
  trackingCtx,
}: {
  slot: SlotContent;
  trackingCtx?: SlotTrackingContext;
}): React.ReactElement | null {
  return resolveSlotComponent(slot, trackingCtx);
}

class RetargetingFallbackErrorBoundary extends React.Component<
  { resetKey: string; children: React.ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  override componentDidCatch(error: Error): void {
    log.error(RETARGETING_FALLBACK_RENDER_ERROR_EVENT, {
      resetKey: this.props.resetKey,
      errorType: error.name,
    });
    this.setState({ failed: true });
  }

  override render(): React.ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

export class RetargetingSlotErrorBoundary extends React.Component<
  RetargetingSlotErrorBoundaryProps,
  RetargetingSlotErrorBoundaryState
> {
  override state: RetargetingSlotErrorBoundaryState = {
    mode: 'primary',
  };

  override componentDidCatch(error: Error, _info: React.ErrorInfo): void {
    log.error(RETARGETING_SLOT_RENDER_ERROR_EVENT, {
      errorType: error.name,
      resetKey: this.props.resetKey,
    });

    this.setState({ mode: 'fallback' });
  }

  override componentDidUpdate(prevProps: RetargetingSlotErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.mode !== 'primary') {
      this.setState({ mode: 'primary' });
    }
  }

  override render(): React.ReactNode {
    if (this.state.mode === 'fallback') {
      const fallbackSlot = buildRetargetingCfFallbackSlot(this.props.slot);
      if (!fallbackSlot) return null;
      return (
        <RetargetingFallbackErrorBoundary resetKey={this.props.resetKey}>
          <SlotResolverChild slot={fallbackSlot} trackingCtx={this.props.trackingCtx} />
        </RetargetingFallbackErrorBoundary>
      );
    }

    return this.props.children ?? <SlotResolverChild slot={this.props.slot} trackingCtx={this.props.trackingCtx} />;
  }
}
